import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Loader2 } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { apiFetch } from '../../lib/api';

// Floating, persistent chat. Two persona bubbles bottom-right; click one to
// open a docked conversation panel. Rendered at App level so it shows on every
// view. One continuous thread per persona, logged server-side in ea_chats.

const SKILL_LABELS = {
  research: 'Research',
  calendar_ops: 'Calendar',
  conflict_detection: 'Conflict check',
  task_capture: 'Task capture',
  email_search: 'Email',
  prioritisation: 'Prioritisation',
  liken_insight: 'Liken insight',
  draft_reply: 'Draft reply',
  finance_snapshot: 'Money snapshot',
  finance_update: 'Money update',
  payoff_scenario: 'Payoff scenario',
  pension_projection: 'Pension projection',
  monthly_budget: 'Monthly budget',
  spending_insights: 'Spending insights',
  import_statement: 'Statement import',
  transaction_search: 'Transactions',
  cashflow_projection: 'Cash projection',
  bank_refresh: 'Bank sync',
};

const PersonaBubble = ({ persona, onClick }) => {
  const accent = persona.accent_color || T.amber;
  return (
    <motion.button
      onClick={onClick}
      title={`Chat with ${persona.name}`}
      whileHover={{ scale: 1.08, y: -2 }}
      whileTap={{ scale: 0.95 }}
      transition={springs.snappy}
      style={{
        width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: `linear-gradient(135deg, ${accent}E6, ${accent})`,
        color: '#fff', fontSize: 19, fontWeight: 700, fontFamily: 'inherit',
        boxShadow: `0 4px 14px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.4)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {persona.name[0]}
    </motion.button>
  );
};

const ChatPanel = ({ persona, onClose }) => {
  const slug = persona.slug;
  const accent = persona.accent_color || T.amber;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`/ea/chat?persona=${slug}`).then(res => res.json());
        if (alive) setMessages(r.messages || []);
      } catch { /* offline — start empty */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages(m => [...m, { id: 'u' + Date.now(), role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await apiFetch('/ea/chat', {
        method: 'POST', body: JSON.stringify({ persona: slug, message: text }),
      });
      const data = await res.json().catch(() => ({}));
      // Surface the server's actual error — a billing or API failure looks
      // nothing like a deploy, and hiding it cost a debugging session once.
      const reply = (res.ok && data.reply)
        ? data.reply
        : data.error
          ? `(${persona.name}'s backend hit an error: ${data.error})`
          : `(Couldn't reach ${persona.name} just now — the backend may be deploying. Try again in a moment.)`;
      setMessages(m => [...m, { id: 'a' + Date.now(), role: 'assistant', content: reply, skills_used: data.skills_used }]);
    } catch (err) {
      setMessages(m => [...m, { id: 'e' + Date.now(), role: 'assistant', content: `(error: ${err.message})` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.96 }}
      transition={springs.gentle}
      style={{
        position: 'fixed', right: 24, bottom: 24, zIndex: 850,
        width: 384, height: 540, maxHeight: 'calc(100vh - 48px)',
        display: 'flex', flexDirection: 'column',
        background: 'rgba(252,251,249,0.98)',
        backdropFilter: 'blur(40px) saturate(180%)',
        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        borderRadius: 18, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.9)',
        boxShadow: '0 16px 50px rgba(40,30,60,0.28)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '13px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)',
        background: `linear-gradient(135deg, ${accent}14, transparent)`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10,
          background: `linear-gradient(135deg, ${accent}E6, ${accent})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 15, fontWeight: 700,
        }}>{persona.name[0]}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{persona.name}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>{persona.role}</div>
        </div>
        <button onClick={onClose} title="Close" style={{
          width: 28, height: 28, borderRadius: 8, border: 'none', cursor: 'pointer',
          background: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><X size={15} color={T.textMuted} /></button>
      </div>

      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {loading ? (
          <div style={{ margin: 'auto', color: T.textMuted, fontSize: 12 }}>Loading…</div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', textAlign: 'center', color: T.textMuted,
            fontSize: 12.5, lineHeight: 1.65, padding: '0 24px' }}>
            Chat with {persona.name} — ask about your day, your week, your goals,
            or anything {persona.name} is tracking.
          </div>
        ) : messages.map(m => (
          <div key={m.id} style={{
            display: 'flex', flexDirection: 'column',
            alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '84%', padding: '8px 12px', borderRadius: 13, fontSize: 13, lineHeight: 1.5,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: m.role === 'user' ? accent : 'rgba(0,0,0,0.05)',
              color: m.role === 'user' ? '#fff' : T.text,
            }}>{m.content}</div>
            {m.role === 'assistant' && m.skills_used && m.skills_used.length > 0 && (
              <div style={{ fontSize: 10, color: T.textLight, marginTop: 3, paddingLeft: 2 }}>
                ⚒ used {m.skills_used.map(s => SKILL_LABELS[s] || s).join(', ')}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: T.textMuted, fontSize: 12, padding: '2px 4px' }}>
            <Loader2 size={13} className="spin" /> {persona.name} is thinking…
          </div>
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '11px 13px', borderTop: '1px solid rgba(0,0,0,0.06)',
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Message ${persona.name}…`}
          style={{
            flex: 1, padding: '9px 13px', borderRadius: 11, fontSize: 13,
            border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.85)',
            fontFamily: 'inherit', color: T.text, outline: 'none',
          }}
        />
        <button onClick={send} disabled={!input.trim() || sending} style={{
          width: 36, height: 36, borderRadius: 10, border: 'none', flexShrink: 0,
          background: (input.trim() && !sending) ? accent : 'rgba(0,0,0,0.06)',
          color: (input.trim() && !sending) ? '#fff' : T.textLight,
          cursor: (input.trim() && !sending) ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Send size={15} /></button>
      </div>
    </motion.div>
  );
};

export const ChatDock = ({ openRequest }) => {
  const [personas, setPersonas] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);

  // A view can ask the dock to open on a persona (Money view -> Penny);
  // `ts` makes repeat clicks on the same persona re-trigger the effect.
  useEffect(() => {
    if (openRequest?.slug) setOpenSlug(openRequest.slug);
  }, [openRequest]);

  useEffect(() => {
    apiFetch('/ea/personas')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d?.personas) {
          setPersonas(d.personas.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        }
      })
      .catch(() => { /* no backend — dock stays hidden */ });
  }, []);

  if (!personas.length) return null;
  const open = personas.find(p => p.slug === openSlug);

  return (
    <>
      <AnimatePresence mode="wait">
        {open && <ChatPanel key={open.slug} persona={open} onClose={() => setOpenSlug(null)} />}
      </AnimatePresence>
      {!open && (
        <div style={{
          position: 'fixed', right: 24, bottom: 92, zIndex: 845,
          display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end',
        }}>
          {personas.map(p => (
            <PersonaBubble key={p.slug} persona={p} onClick={() => setOpenSlug(p.slug)} />
          ))}
        </div>
      )}
    </>
  );
};
